import { Box, Card, CardActionArea, Grid } from '@material-ui/core';
import classNames from 'classnames';
import React, { FC } from 'react';
import { IconCheckCircleFilled, TypographyWrapper } from '..';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import { Product as ShopifyProduct, ShopifyProductVariant } from '../../types/shopify/product';
import { getProductDisplayTitle, getStockImages } from '../../util/shopifyHelpers';

import css from './Shopify.module.css';
import { useDefaultShopStyles } from '../../hooks/useDefaultShopStyles';

interface TitleProps {
  product?: ShopifyProduct;
  variant?: ShopifyProductVariant;
  productTitleDisplayType: string;
  size: string;
}

const Title: FC<TitleProps> = (props) => {
  const { product, productTitleDisplayType, size, variant } = props;

  return (
    <TypographyWrapper
      variant="body1"
      typographyOverrides={{ style: { textAlign: 'center' } }}
      isDynamicValue
    >
      {`${getProductDisplayTitle(product, productTitleDisplayType, variant)}${
        size ? ` | ${size}` : ''
      }`}
    </TypographyWrapper>
  );
};

interface ProductImageAndTitleProps {
  isSelected: boolean;
  size: string;
  product?: ShopifyProduct;
  variant?: any;
}

const ProductImageAndTitle: FC<ProductImageAndTitleProps> = (props) => {
  const { isSelected, product, size, variant } = props;
  const defaultShopStyles = useDefaultShopStyles();

  const { imageRatio, productTitleDisplayType, chooseStockImagesOption } = useShopConfig();

  const productImages = getStockImages(product, variant, chooseStockImagesOption);
  const featuredProductImage = productImages[0] || product?.featuredImage?.originalSrc;

  return (
    <div
      className={classNames(css.product, {
        [css.selectedProductImage]: isSelected,
      })}
    >
      <div className={css.threeToTwoWrapper}>
        <div style={{ paddingBottom: `${imageRatio * 100}%` }}>
          {/* eslint-disable jsx-a11y/img-redundant-alt */}
          <img className={css.productImage} src={featuredProductImage} alt="product photo" />
        </div>
      </div>
      {isSelected && (
        <IconCheckCircleFilled
          color={defaultShopStyles.shopColor100}
          className={css.selectedCheckMark}
        />
      )}

      <Box pt={1}>
        <Title
          product={product}
          variant={variant}
          productTitleDisplayType={productTitleDisplayType}
          size={size}
        />
      </Box>
    </div>
  );
};

interface ProductTitleProps {
  isSelected: boolean;
  size: string;
  product?: ShopifyProduct;
}

const ProductTitle: FC<ProductTitleProps> = (props) => {
  const { isSelected, product, size } = props;

  const { productTitleDisplayType } = useShopConfigV2();

  return (
    <div
      className={classNames(css.productTitle, {
        [css.selectedProductImage]: isSelected,
      })}
    >
      <Card variant="outlined" style={{ height: '100%', width: '100%' }}>
        <CardActionArea style={{ padding: '22px', height: '100%', width: '100%' }}>
          <Title product={product} productTitleDisplayType={productTitleDisplayType} size={size} />
        </CardActionArea>
      </Card>
    </div>
  );
};

interface ProductProps {
  product?: ShopifyProduct;
  selectedProductId?: string;
  lineItemId?: string;
  selectedLineItemId?: string;
  variant?: any;
  onChange?: (product?: ShopifyProduct, variant?: any, lineItemId?: string) => void;
}

const Product: FC<ProductProps> = (props: ProductProps) => {
  const { product, selectedProductId, lineItemId, selectedLineItemId, variant, onChange } = props;

  const selectedOptions = variant?.selectedOptions;
  const size = selectedOptions?.find(
    (option: any) => option?.name?.toLowerCase() === 'size'
  )?.value;
  const isSelected =
    product?.id === selectedProductId && (!selectedLineItemId || lineItemId === selectedLineItemId);

  const handleClick = () => {
    if (onChange) {
      onChange(product, variant, lineItemId);
    }
  };

  return (
    <Grid
      onClick={handleClick}
      style={{ marginBottom: 16 }}
      item
      xs={6}
      md={3}
      key={product?.id}
      role="button"
      tabIndex={0}
    >
      {product?.featuredImage?.originalSrc ? (
        <ProductImageAndTitle
          isSelected={isSelected}
          product={product}
          variant={variant}
          size={size}
        />
      ) : (
        <ProductTitle isSelected={isSelected} product={product} size={size} />
      )}
    </Grid>
  );
};

export default Product;
